// Shared utilities for DoctorDashboard and Hospital_DoctorDashboard

/**
 * Average emotion scores across multiple 8-second audio segment results
 * @param {Array} results - Array of segment result objects with emotion scores
 * @returns {Object|null} Averaged emotions and burnout score, or null if empty
 */
export function averageSegments(results) {
  if (!results || results.length === 0) return null;
  const keys = ['joy', 'stress', 'sadness', 'anger', 'hurt'];
  const avg = keys.reduce((acc, k) => ({
    ...acc,
    [k]: results.reduce((s, r) => s + (r[k] || 0), 0) / results.length
  }), {});
  const burnout = Math.round(results.reduce((s, r) => s + (r.burnout_score || 0), 0) / results.length);
  return { emotions: avg, burnout_score: burnout };
}

/**
 * Prepare chart data from patient report data
 * @param {Object} patient - Patient object with emotionVector, weeklyReport, monthlyReport
 * @param {Function} calculateScore - Function to calculate overall mental health score
 * @returns {Object} { emotionData, weeklyData, monthlyData }
 */
export function prepareReportChartData(patient, calculateScore) {
  if (!patient) {
    return { emotionData: [], weeklyData: [], monthlyData: [] };
  }

  const emotionData = Object.entries(patient.emotionVector || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: value
  }));

  const weeklyData = (patient.weeklyReport || []).map((item, index) => ({
    day: `Day ${index + 1}`,
    score: typeof item === 'object' ? calculateScore(item.emotions) : item,
    emotions: typeof item === 'object' ? item.emotions : null,
    sessionDate: typeof item === 'object' ? item.sessionDate : null
  }));

  const monthlyData = (patient.monthlyReport || []).map((item, index) => ({
    month: typeof item === 'object' && item.month
      ? new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      : `Month ${index + 1}`,
    score: typeof item === 'object' ? calculateScore(item.emotions) : item,
    emotions: typeof item === 'object' ? item.emotions : null,
    sessionCount: typeof item === 'object' ? item.sessionCount : 0
  }));

  return { emotionData, weeklyData, monthlyData };
}

/**
 * Generate emotion trend data from session history
 * @param {Array} sessionHistory - Array of session objects with emotion data
 * @returns {Array} Array of trend data objects for Recharts
 */
export function generateEmotionTrendData(sessionHistory) {
  if (!sessionHistory || sessionHistory.length === 0) return [];
  
  return sessionHistory
    .filter(session => session.emotion)
    .slice(0, 10)
    .reverse()
    .map((session, index) => ({
      session: `S${index + 1}`,
      date: session.session_date ? new Date(session.session_date).toLocaleDateString() : `Session ${index + 1}`,
      joy: Math.round(session.emotion.joy),
      stress: Math.round(session.emotion.stress),
      sadness: Math.round(session.emotion.sadness),
      anger: Math.round(session.emotion.anger),
      burnout: Math.round(session.emotion.burnout_score)
    }));
}
